import PolicyApi from "../../services/PolicyAPI";
import Logger from "../../../shared/services/Logger";
import { PolicyEndorsementsReadDto } from "../../model/PolicyEndorsementsReadDto";
import { PolicyCoverageReadDto } from "../../model/PolicyCoverageReadDto";
import { PolicyTransactionsReadDto } from "../../model/PolicyTransactionsReadDto";
import { formatWithCommas, fomratWithCommasAndDecimals } from "../../../shared/util/currencyUtils";
import { PolicyLOBReadDto } from "../../model/PolicyLOBReadDto";
import { PolicyLocationReadDto } from "../../model/PolicyLocationReadDto";
import { PolicyUnderwritingReadDto } from "../../model/PolicyUnderwritingReadDto";
import { PolicyAdditionalInterestDto } from "../../model/PolicyAdditionalInterestDto";
import { PolicyFloodCoverageDto } from "../../model/PolicyFloodCoverageDto";
import { PolicyFloodUnderwritingDto } from "../../model/PolicyFloodUnderwritingDto";

class CustomerPolicyDetailViewService {
  polciAPI: PolicyApi = new PolicyApi();

  private processCoverages = (policyCoveragesAndEndorsementsAPIResponse: PolicyCoverageReadDto[]): PolicyCoverageReadDto[] => {
    let policyCoverages: PolicyCoverageReadDto[] = policyCoveragesAndEndorsementsAPIResponse
        .filter(policyCoverage => policyCoverage.isCoverage === true)
        .map(item => {
            let dedTypeAmt1, dedTypeAmt2, dedTypeAmt3 = '';
            if (item.deductibleType1) {
                dedTypeAmt1 = `${item.deductibleType1Value}/${formatWithCommas(item.deductible1 ?? 0)}`;
            }
            if (item.deductibleType2) {
                dedTypeAmt2 = `${item.deductibleType2Value}/${formatWithCommas(item.deductible2 ?? 0)}`;
            }
            if (item.deductibleType3) {
                dedTypeAmt3 = `${item.deductibleType3Value}/${formatWithCommas(item.deductible3 ?? 0)}`;
            }
            return { ...item, dedTypeAmt1, dedTypeAmt2, dedTypeAmt3 };
        });

    return policyCoverages;
};

private processEndorsements = (policyCoveragesAndEndorsementsAPIResponse: PolicyCoverageReadDto[]): PolicyEndorsementsReadDto[] => {
    let policyEndorsements: PolicyEndorsementsReadDto[] = policyCoveragesAndEndorsementsAPIResponse
        .filter(policyEndorsement => policyEndorsement.isCoverage === false)
        .map(item => {
            let dedTypeAmt = '';
            if (item.deductibleType1) {
                dedTypeAmt = `${item.deductibleType1Value}/${formatWithCommas(item.deductible1 ?? 0)}`;
            } else if (item.deductibleType2) {
                dedTypeAmt = `${item.deductibleType2Value}/${formatWithCommas(item.deductible2 ?? 0)}`;
            } else if (item.deductibleType3) {
                dedTypeAmt = `${item.deductibleType3Value}/${formatWithCommas(item.deductible3 ?? 0)}`;
            }
            return { ...item, dedTypeAmt };
        });
    return policyEndorsements;
};

  getTransactionsDetails = async (policyId: string): Promise<PolicyTransactionsReadDto[] | null> => {
    if (!policyId) return null;
    try {
        const policyTransactionsReadAPIResponse: PolicyTransactionsReadDto[] = await this.polciAPI.fetchPolicyTransactionsById(policyId);
        return policyTransactionsReadAPIResponse
    } catch (error) {
        Logger.logError(error.toString());
        throw error;
    }
};

  getLOBs = async (policyId: string, seqNum : Number): Promise<PolicyLOBReadDto[] | null> => {
    if (!policyId) return null;
    try {
        const policyLOBsReadAPIResponse: PolicyLOBReadDto[] = await this.polciAPI.fetchPolicyLOBsById(policyId, seqNum);
        return policyLOBsReadAPIResponse
    } catch (error) {
        Logger.logError(error.toString());
        throw error;
    }
};

getLocations = async (policyId: string, lobId: string, seqNum : Number): Promise<PolicyLocationReadDto[] | null> => {
    if (!policyId || !lobId) return null;
    try {
        const policyLocationsReadAPIResponse: PolicyLocationReadDto[] = await this.polciAPI.fetchPolicyLocationsByLOB(policyId, lobId, seqNum);
        return policyLocationsReadAPIResponse
    } catch (error) {
        Logger.logError(error.toString());
        throw error;
    }
};

getCoveragesAndEndorsements = async (policyId: string, lobId: string, seqNum : Number): Promise<{ coverages: PolicyCoverageReadDto[]; endorsements: PolicyEndorsementsReadDto[] }> => {
    if (!policyId || !lobId) return null;
    try {
        let policyCoveragesAndEndorsementsAPIResponse: PolicyCoverageReadDto[] = await this.polciAPI.fetchPolicyCoveragesAndEndorsementsByLOB(policyId, lobId, seqNum);

        let policyCoverages : PolicyCoverageReadDto[] = this.processCoverages(policyCoveragesAndEndorsementsAPIResponse);
        let policyEndorsements : PolicyEndorsementsReadDto[] =  this.processEndorsements(policyCoveragesAndEndorsementsAPIResponse);

        return { coverages: policyCoverages, endorsements: policyEndorsements };

    } catch (error) {
        Logger.logError(error.toString());
        throw error;
    }
};

getUnderwriting = async (policyId: string, lobId: string, locId : string, seqNum : Number): Promise<PolicyUnderwritingReadDto> => {
    if (!policyId || !lobId || !locId) return null;
    try {
        let policyUnderwritingAPIResponse: PolicyUnderwritingReadDto = await this.polciAPI.fetchPolicyUnderwritingByLocation(policyId, lobId, locId, seqNum);
        return policyUnderwritingAPIResponse

    } catch (error) {
        Logger.logError(error.toString());
        throw error;
    }
};

getAdditionalInterests = async (policyId: string, lobId: string, seqNum : Number): Promise<PolicyAdditionalInterestDto[] | null> => {
    if (!policyId || !lobId) return null;
    try {
        const policyAdditioanlInterestsReadAPIResponse: PolicyAdditionalInterestDto[] = await this.polciAPI.fetchPolicyAdditionalInterestsByLOB(policyId, lobId, seqNum);
        return policyAdditioanlInterestsReadAPIResponse
    } catch (error) {
        Logger.logError(error.toString());
        throw error;
    }
};

getFloodCoverages = async (policyId: string, lobId: string, polEventId : string): Promise<{ coverages: PolicyFloodCoverageDto[];}> => {    
    if (!policyId || !lobId) return null;
    try {
        let policyFloodCoveragesAPIResponse: PolicyFloodCoverageDto[] = await this.polciAPI.fetchPolicyFloodCoveragesByLOB(policyId, lobId, polEventId);       
        return { coverages: policyFloodCoveragesAPIResponse };

    } catch (error) {
        Logger.logError(error.toString());
        throw error;
    }
};

getPolicyFloodUnderwritingByLocation = async (policyId: string, lobId: string, locId: string, seqNum: number): Promise<PolicyFloodUnderwritingDto> => {
        if (!policyId || !lobId || !locId || !seqNum) return null;
        try {
            const response: PolicyFloodUnderwritingDto = await this.polciAPI.fetchPolicyFloodUnderwritingByLocation(policyId, lobId, locId, seqNum);
            return response;
        } catch (error) {
            Logger.logError(error.toString());
            throw error;
        }
    };
}

export default CustomerPolicyDetailViewService;